import { runCheck } from "./checks.js";
import { evalCondition, isFilled, resolveText } from "./conditions.js";
import type { FieldSpec, Issue, Rule, RuleContext, Severity, Transform, Value, Values } from "./types.js";

/* ---------------- transforms ---------------- */

const LIVE: ReadonlySet<Transform> = new Set(["stripSpaces", "upper", "digitsAndDashes", "dateMDY"]);

function formatMDY(raw: string): string {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  if (d.length > 4) return `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4)}`;
  if (d.length > 2) return `${d.slice(0, 2)}-${d.slice(2)}`;
  return d;
}

function applyOne(t: Transform, s: string): string {
  switch (t) {
    case "trim": return s.trim();
    case "collapseSpaces": return s.replace(/\s+/g, " ");
    case "stripSpaces": return s.replace(/\s+/g, "");
    case "upper": return s.toUpperCase();
    case "digitsAndDashes": return s.replace(/[^\d-]/g, "");
    case "dateMDY": return formatMDY(s);
  }
}

/** What the input shows while the person types (no trimming, so they can type a space). */
export function liveTransform(spec: FieldSpec, raw: string): string {
  return (spec.transforms ?? []).filter((t) => LIVE.has(t)).reduce((s, t) => applyOne(t, s), raw);
}

/** The stored value: every transform, applied on blur and again on the server. */
export function normalize(spec: FieldSpec, raw: Value): Value {
  if (typeof raw !== "string") return raw;
  return (spec.transforms ?? []).reduce((s, t) => applyOne(t, s), raw);
}

/* ---------------- dates ---------------- */

export function parseMDY(s: string): Date | null {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(s);
  if (!m) return null;
  const mo = Number(m[1]), d = Number(m[2]), y = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return dt;
}

/** Whole years from `birth` to `now`, counting a birthday only once it has happened. */
export function ageInYears(birth: Date, now: Date): number {
  let age = now.getUTCFullYear() - birth.getUTCFullYear();
  const beforeBirthday =
    now.getUTCMonth() < birth.getUTCMonth() ||
    (now.getUTCMonth() === birth.getUTCMonth() && now.getUTCDate() < birth.getUTCDate());
  if (beforeBirthday) age -= 1;
  return age;
}

/* ---------------- visibility and required ---------------- */

export function isVisible(spec: FieldSpec, values: Values): boolean {
  return spec.visibleWhen ? evalCondition(spec.visibleWhen, values) : true;
}

function policyRequires(spec: FieldSpec, values: Values, ctx: RuleContext): boolean {
  return (ctx.policy?.alsoRequire ?? []).some((p) =>
    typeof p === "string" ? p === spec.id : p.field === spec.id && (!p.when || evalCondition(p.when, values)),
  );
}

export function isRequired(spec: FieldSpec, values: Values, ctx: RuleContext): boolean {
  if (!isVisible(spec, values)) return false;
  if (policyRequires(spec, values, ctx)) return true;
  return spec.rules.some((r) => r.kind === "required" && (!r.when || evalCondition(r.when, values)));
}

export function requiredFieldIds(specs: FieldSpec[], values: Values, ctx: RuleContext): string[] {
  return specs.filter((s) => isRequired(s, values, ctx)).map((s) => s.id);
}

function requiredMessage(spec: FieldSpec, values: Values, ctx: RuleContext): string {
  const own = spec.rules.find((r): r is Extract<Rule, { kind: "required" }> => r.kind === "required");
  return ctx.policy?.requiredMessages?.[spec.id] ?? (own ? resolveText(own.message, values) : `Fill in “${resolveText(spec.label, values)}”.`);
}

/* ---------------- validation ---------------- */

function issue(field: string, severity: Severity, message: string, rule: string): Issue {
  return { field, severity, message, rule };
}

/**
 * Checks one field against the current answers. Stops at the first error (one clear
 * message at a time); warnings and flags from earlier rules are kept.
 */
export function validateField(spec: FieldSpec, values: Values, ctx: RuleContext): Issue[] {
  if (!isVisible(spec, values)) return [];
  const raw = normalize(spec, values[spec.id]);
  if (!isFilled(raw)) {
    return isRequired(spec, values, ctx) ? [issue(spec.id, "error", requiredMessage(spec, values, ctx), "required")] : [];
  }
  if (typeof raw === "boolean") return [];
  const v = raw as string;
  const out: Issue[] = [];
  for (const r of spec.rules) {
    if (r.kind === "required") continue;
    if (r.when && !evalCondition(r.when, values)) continue;
    const found = runRule(spec, r, v, values, ctx);
    if (!found) continue;
    out.push(found);
    if (found.severity === "error") break;
  }
  return out;
}

function runRule(spec: FieldSpec, r: Rule, v: string, values: Values, ctx: RuleContext): Issue | null {
  const sev = ("severity" in r && r.severity) || "error";
  const msg = (t: Parameters<typeof resolveText>[0]) => resolveText(t, values);
  switch (r.kind) {
    case "required":
      return null;
    case "pattern":
      return new RegExp(r.pattern, r.flags).test(v) ? null : issue(spec.id, sev, msg(r.message), "pattern");
    case "notPattern":
      return new RegExp(r.pattern, r.flags).test(v) ? issue(spec.id, sev, msg(r.message), "notPattern") : null;
    case "oneOf":
      return r.values.includes(v) ? null : issue(spec.id, sev, msg(r.message), "oneOf");
    case "notOneOf":
      return r.values.includes(v) ? issue(spec.id, sev, msg(r.message), "notOneOf") : null;
    case "length": {
      const n = [...v].length;
      return (r.min !== undefined && n < r.min) || (r.max !== undefined && n > r.max) ? issue(spec.id, sev, msg(r.message), "length") : null;
    }
    case "number": {
      if (!/^-?\d+(\.\d+)?$/.test(v)) return issue(spec.id, sev, msg(r.message), "number");
      const n = Number(v);
      return (r.min !== undefined && n < r.min) || (r.max !== undefined && n > r.max) ? issue(spec.id, sev, msg(r.message), "number") : null;
    }
    case "date": {
      const d = parseMDY(v);
      if (!d) return issue(spec.id, "error", msg(r.message), "date");
      if (r.notFuture && d.getTime() > ctx.now.getTime()) return issue(spec.id, "error", msg(r.notFuture), "date.future");
      const age = ageInYears(d, ctx.now);
      if (r.minAge && age < r.minAge.years) return issue(spec.id, "error", msg(r.minAge.message), "date.minAge");
      if (r.maxAge && age > r.maxAge.years) return issue(spec.id, "error", msg(r.maxAge.message), "date.maxAge");
      return null;
    }
    case "check": {
      const m = runCheck(r.check, v, values, ctx, r.params);
      return m ? issue(spec.id, sev, m, `check:${r.check}`) : null;
    }
  }
}

export interface Validation {
  issues: Issue[];
  byField: Record<string, Issue[]>;
  errors: number;
  warnings: number;
  flags: number;
  /** Required fields that are filled with no error, out of all required fields. */
  progress: { done: number; total: number };
}

/** Every answer in its stored form, defaults filled in, hidden ones kept (conditions may read them). */
export function normalizeAll(specs: FieldSpec[], values: Values): Values {
  const out: Values = { ...values };
  for (const s of specs) out[s.id] = normalize(s, values[s.id] ?? s.defaultValue ?? (s.type === "toggle" ? false : ""));
  return out;
}

export function validateAll(specs: FieldSpec[], rawValues: Values, ctx: RuleContext): Validation {
  const values = normalizeAll(specs, rawValues);
  const issues: Issue[] = [];
  const byField: Record<string, Issue[]> = {};
  for (const s of specs) {
    const found = validateField(s, values, ctx);
    if (found.length) byField[s.id] = found;
    issues.push(...found);
  }
  const req = requiredFieldIds(specs, values, ctx);
  const done = req.filter((id) => isFilled(values[id]) && !(byField[id] ?? []).some((i) => i.severity === "error")).length;
  return {
    issues,
    byField,
    errors: issues.filter((i) => i.severity === "error").length,
    warnings: issues.filter((i) => i.severity === "warning").length,
    flags: issues.filter((i) => i.severity === "flag").length,
    progress: { done, total: req.length },
  };
}

/** The answers to keep: normalised, hidden fields dropped, defaults applied. */
export function cleanValues(specs: FieldSpec[], values: Values): Values {
  const withDefaults: Values = {};
  for (const s of specs) withDefaults[s.id] = values[s.id] ?? s.defaultValue ?? (s.type === "toggle" ? false : "");
  const out: Values = {};
  for (const s of specs) {
    if (!isVisible(s, withDefaults)) continue;
    out[s.id] = normalize(s, withDefaults[s.id]);
  }
  return out;
}

/** Live chips under an input: which parts of the format are already right. */
export function hintStates(spec: FieldSpec, values: Values): { id: string; label: string; ok: boolean }[] {
  const v = typeof values[spec.id] === "string" ? liveTransform(spec, values[spec.id] as string) : "";
  return (spec.hints ?? [])
    .filter((h) => !h.when || evalCondition(h.when, values))
    .map((h) => ({ id: h.id, label: h.label, ok: new RegExp(h.pattern, h.flags).test(v) }));
}
